// Shared shell for the 5 new Academy tool pages. Mirrors the layout of
// AcademyPatronesPage so the visual language (header, ambient background,
// breadcrumb, footer hint) is consistent across the whole Academy.
//
// Adds two cross-cutting behaviors so the 5 pages stay free of duplication:
//   • `requirePro`: when true, non-PRO users see the PremiumGate paywall
//     (same component used by the AI Mentor tab) instead of the children.
//   • Pulls `lang` from the LanguageContext and exposes it via the
//     `getLang()` prop callback so children can localize their own copy.
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, GraduationCap } from "lucide-react";
import { ASSETS } from "../../assets";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { SimulatorRankCard } from "../SimulatorRankCard";
import { useLanguage } from "../../i18n/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { PremiumGate } from "../PremiumGate";
import { getAcademyToolsI18n } from "../../i18n/academy-tools";

export const AcademyShell = ({
  testid,
  eyebrow,
  eyebrowIcon: EyebrowIcon = GraduationCap,
  title,
  subtitle,
  breadcrumbCurrent,
  footHint,
  requirePro = true,
  children,
}) => {
  const { lang } = useLanguage();
  const { isPro } = useAuth();
  const T = getAcademyToolsI18n(lang);
  const gated = requirePro && !isPro;
  return (
  <div className="relative min-h-screen overflow-x-hidden bg-black text-white" data-testid={testid}>
    {/* Ambient background — identical to AcademyPage */}
    <div className="pointer-events-none fixed inset-0">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(255,140,0,0.18),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_50%,rgba(168,85,247,0.10),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_70%,rgba(0,229,255,0.10),transparent_55%)]" />
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />
    </div>

    {/* HEADER — same proportions as AcademyPatronesPage */}
    <header className="relative z-20 border-b border-white/5 bg-black/60 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-10 md:py-4">
        <Link
          to="/academy"
          className="group inline-flex items-center gap-2 text-white/70 transition-colors hover:text-white"
          data-testid={`${testid}-back`}
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-xs font-bold uppercase tracking-[0.2em]">{T.back}</span>
        </Link>
        <div className="flex items-center gap-3">
          <img src={ASSETS.logos.rebel} alt="REBEL" className="h-8 w-auto md:h-10" />
        </div>
        <div className="flex items-center gap-3">
          <SimulatorRankCard />
          <LanguageSwitcher />
        </div>
      </div>
    </header>

    <main className="relative z-10 mx-auto max-w-7xl px-4 py-8 md:px-10">
      <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
        <Link to="/academy" className="transition-colors hover:text-orange-300">
          {T.back.replace(/^Volver a la |^Späť na |^Back to /, "")}
        </Link>
        <span className="px-2 text-white/25">→</span>
        <span className="text-orange-300">{breadcrumbCurrent}</span>
      </p>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="mb-8"
      >
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-400/[0.08] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.3em] text-orange-300 backdrop-blur-sm">
          <EyebrowIcon className="h-3 w-3" />
          {eyebrow}
        </div>
        <h2 className="text-3xl font-black tracking-tight text-white md:text-5xl">{title}</h2>
        {subtitle && <p className="mt-3 max-w-2xl text-sm text-white/55">{subtitle}</p>}
      </motion.div>

      {gated ? (
        <div className="mx-auto max-w-2xl py-4" data-testid={`${testid}-gate`}>
          <PremiumGate feature="mentor" blur={false} />
        </div>
      ) : (
        typeof children === "function" ? children({ lang, T }) : children
      )}

      {!gated && footHint && (
        <div className="mt-12 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
          <GraduationCap className="h-3.5 w-3.5 text-orange-300" />
          {footHint}
        </div>
      )}
    </main>
  </div>
  );
};

export default AcademyShell;
